import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { PatientDto, PatientMinimalDto, PatientsApi, RxDto } from "../../api-client";
import { WrappedApiClient } from "../../api/proxiedClient";
import { useApi } from "../../api/ApiProvider";
import { patientKeys } from "./patient.keys";
import { toRxWriteDto } from "../../helpers/rxWriteDto";

/**
 * Hook to fetch a single patient by ID
 * Replaces PatientsStore.getPatient
 *
 * Pass the active locationId so the API can return the correct sublocality
 * (sublocalities are unique per address + location).
 */
export const usePatient = (id: number, enabled: boolean = true, locationId?: string) => {
  const { patients } = useApi();

  return useQuery<PatientDto>({
    queryKey: id !== undefined ? [...patientKeys.patient.detail(id), { locationId }] : ["patient", { id: undefined }],
    queryFn: async () => await patients.getPatient(id, locationId),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Patient konnte nicht geladen werden" },
  });
};

/**
 * Imperative fetch for patient minimal data. Can be used outside React components.
 * Checks individual caches first, batch-fetches only missing IDs, seeds per-patient caches.
 */
export async function fetchPatientsMinimal(
  queryClient: QueryClient,
  patientsApi: WrappedApiClient<PatientsApi>,
  patientIds: number[],
): Promise<PatientMinimalDto[]> {
  if (patientIds.length === 0) return [];

  const cached: PatientMinimalDto[] = [];
  const missing: number[] = [];

  for (const id of patientIds) {
    const existing = queryClient.getQueryData<PatientMinimalDto>(
      patientKeys.patientMinimal.detail(id),
    );
    if (existing) {
      cached.push(existing);
    } else {
      missing.push(id);
    }
  }

  let fetched: PatientMinimalDto[] = [];
  if (missing.length > 0) {
    const BATCH_SIZE = 50;
    for (let i = 0; i < missing.length; i += BATCH_SIZE) {
      const batch = missing.slice(i, i + BATCH_SIZE);
      const response = await patientsApi.getPatientsMinimalByIds({ patientIds: batch });
      const batchResult: PatientMinimalDto[] = (response as any)?.data ?? response ?? [];

      for (const patient of batchResult) {
        queryClient.setQueryData(patientKeys.patientMinimal.detail(patient.id), patient);
      }
      fetched.push(...batchResult);
    }
  }

  return [...cached, ...fetched];
}

export const usePatientsMinimal = (patientIds: number[], enabled: boolean = true) => {
  const { patients } = useApi();
  const queryClient = useQueryClient();

  // Ensure individual patient-minimal entries have proper cache lifetimes
  queryClient.setQueryDefaults(patientKeys.patientMinimal.all(), {
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  return useQuery<PatientMinimalDto[]>({
    queryKey: patientKeys.patientMinimal.batch(patientIds),
    queryFn: () => fetchPatientsMinimal(queryClient, patients, patientIds),
    enabled: enabled && patientIds.length > 0,
    staleTime: 0,
    gcTime: 0,
    meta: { errorMessage: "Patientendaten konnten nicht geladen werden" },
  });
};

export const usePatientRxSeries = (patientId: number, enabled: boolean = true) => {
  const { patients } = useApi();
  return useQuery<any>({
    queryKey: patientKeys.patientRxSeries.detail(patientId),
    queryFn: async () => {
      const patient = await patients.getPatientRxSeriesData(patientId);
      return patient || [];
    },
    enabled: enabled && !!patientId,
    meta: { errorMessage: "Rezeptseriendaten konnten nicht geladen werden" },
  });
};
/** * Hook to fetch patient waitlist data
 * Replaces PatientsStore.getPatientWaitlistStats
 */

export const usePatientWaitlistStats = (patientId: number, enabled: boolean = true) => {
  const { patients } = useApi();
  return useQuery<any>({
    queryKey: patientKeys.patientWaitlistStats.detail(patientId),
    queryFn: async () => {
      const stats = await patients.getPatientWaitlistStats(patientId);
      return stats;
    },
    enabled: enabled && !!patientId,
    meta: { errorMessage: "Wartelistenstatistiken konnten nicht geladen werden" },
  });
};

/**
 * Hook to fetch a patient by RX ID
 * Replaces PatientsStore.getPatientByRxId
 */
export const usePatientByRxId = (rxId: string, enabled: boolean = true) => {
  const { patients } = useApi();

  return useQuery<PatientDto>({
    queryKey: rxId
      ? patientKeys.patientByRxId.detail(rxId)
      : ["patientByRxId", { rxId: undefined }],
    queryFn: () => patients.getPatientByRxId(rxId),
    enabled: enabled && !!rxId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: "Patientendaten konnten nicht abgerufen werden" },
  });
};

/**
 * Hook to fetch all patients
 * Replaces PatientsStore.getAllPatients
 */
export const useAllPatients = (enabled: boolean = true) => {
  const { patients } = useApi();

  return useQuery<PatientMinimalDto[]>({
    queryKey: patientKeys.allPatients.all(),
    queryFn: () => patients.getAllPatients(),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes - patient list changes frequently
    meta: { errorMessage: "Patientenliste konnte nicht geladen werden" },
  });
};

/**
 * Hook to fetch archived patients with pagination
 * Replaces PatientsStore.getArchivedPatients
 */
export const useArchivedPatients = (
  page: number = 1,
  limit: number = 50,
  search?: string,
  enabled: boolean = true,
) => {
  const { patients } = useApi();

  return useQuery({
    queryKey: patientKeys.archivedPatients.detail(page, limit, search),
    queryFn: async () => {
      const result = await patients.getArchivedPatients(page, limit, search);
      return result || { patients: [], total: 0, page: 1, limit: 50 };
    },
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
    meta: { errorMessage: "Fehler beim Abrufen der archivierten Patienten" },
  });
};

/**
 * Hook to fetch RX statistics
 * Replaces PatientsStore.getRxStats
 */
export const useRxStats = (enabled: boolean = true) => {
  const { patients } = useApi();

  return useQuery({
    queryKey: patientKeys.rxStats.all(),
    queryFn: async () => patients.getRxStats(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - stats don't change frequently
    meta: { errorMessage: "RX-Statistiken konnten nicht geladen werden" },
  });
};

/**
 * Hook to get live validation for RX data
 * Note: This is kept as a function call since it's typically used for form validation
 * and doesn't need caching like other queries
 */
export const useLiveValidation = () => {
  const { patients } = useApi();

  return {
    getLiveValidation: async (rx: RxDto) => {
      // Remove empty strings; for GKV Rxs also remove partial (<9 char) entries that would fail class validator
      const rxTypeText = rx.rxtype?.text || "";
      const isNonGKV = ["Privat/Selbstzahler", "Berufsgenossenschaft", "Bundeswehr"].includes(
        rxTypeText,
      );
      if (rx.doctorId === "") {
        rx.doctorId = undefined;
      } else if (!isNonGKV && rx.doctorId && rx.doctorId.length < 9) {
        rx.doctorId = undefined;
      }
      if (rx.clinicId === "") {
        rx.clinicId = undefined;
      } else if (!isNonGKV && rx.clinicId && rx.clinicId.length < 9) {
        rx.clinicId = undefined;
      }

      const result = await patients.getLiveValidation(toRxWriteDto(rx));
      return Array.isArray(result) ? result : [];
    },
  };
};

/**
 * @deprecated Use useRxsForPatient from rx.queries.ts instead
 * This hook fetches full patient data just to get RX list, which is inefficient
 * Hook to fetch RX data for a patient with optional filtering for deleted RXs
 * This replaces the need to fetch patient data just to get RX list
 */
export const usePatientRxs = (patientId: number, includeDeleted: boolean = false) => {
  const { patients } = useApi();

  return useQuery<RxDto[]>({
    queryKey: patientKeys.patient.rxs(patientId, includeDeleted),
    queryFn: async () => {
      const patient = await patients.getPatient(patientId);
      if (!patient?.rxs) return [];

      // Filter based on includeDeleted flag
      return includeDeleted ? patient.rxs : patient.rxs.filter((rx: any) => rx.deletedAt === null);
    },
    enabled: !!patientId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    meta: { errorMessage: "Rezeptdaten konnten nicht geladen werden" },
  });
};
